import { StatusIndicator } from '@diligentcorp/atlas-theme-mui-lens';
import 'filepond/dist/filepond.min.css';
import React, { useMemo } from 'react';
import { OnChangeValue } from 'react-select';
import {
  Checkbox,
  HelpText,
  RadioButton,
  TextArea,
  TextInput,
} from 'src/components/atoms';
import {
  DataGridInput,
  DatePicker,
  DropdownSelect,
  TimePicker,
} from 'src/components/molecules';
import { FormState } from 'src/types';
import { NUMERIC_QUESTION_MAX } from 'src/types/formQuestionnaire';
import {
  DateTimeRow,
  DynamicQuestionDescription,
  DynamicQuestionLabel,
  OptionsList,
  ReadOnlyCard,
} from './DynamicQuestion.styles';
import { DynamicQuestionDatabaseField } from './DynamicQuestionDatabaseField';
import { DynamicQuestionFileUploadField } from './DynamicQuestionFileUploadField';
import { overdueMitigationColorResolver } from 'src/components/atlas/organisms/helpers/sharedStatusColorResolvers';
import { DynamicQuestionProps, sortOptions } from './DynamicQuestionProps';
import { DynamicQuestionReadOnlyValue } from './DynamicQuestionReadOnlyValue';
import { QuestionTypeTypeKey } from './types';
import { useDynamicQuestionEvidencePond } from './useDynamicQuestionEvidencePond';

const MAX_NUMBER = NUMERIC_QUESTION_MAX;

export const DynamicQuestion: React.FC<DynamicQuestionProps> = ({
  question,
  value,
  onChange,
  fileUploadHelpArticleUrl,
  reportIdForEvidenceUpload,
  reportIdForFileDownload,
  fileDownloadCaseManagerView = false,
  readOnly = false,
  showValidation = false,
  onRegisterQuestionnaireAttachment,
  onDeleteQuestionnaireAttachment,
  'data-testid': dataTestId,
  isFlagged = false,
}) => {
  const { syncEvidenceFilesFromPond, handleRemoveFileFromPond, handleSetPond } =
    useDynamicQuestionEvidencePond({
      question,
      value,
      onChange,
      readOnly,
      onRegisterQuestionnaireAttachment,
      onDeleteQuestionnaireAttachment,
    });

  const typeKey = question.questionType?.typeKey ?? null;
  const questionId = question.id ?? '';
  const baseTestId = dataTestId ?? `dynamic-question-${questionId}`;
  const label = question.questionText ?? '';
  const description = question.description ?? undefined;
  const isRequired = Boolean(question.isRequired);

  const options = useMemo(
    () =>
      sortOptions(question.options ?? []).filter(
        (o) => o.id != null && o.optionText != null,
      ),
    [question.options],
  );

  const dropdownOptions = useMemo(
    () =>
      options.map((o) => ({
        value: o.id!,
        label: o.optionText ?? '',
      })),
    [options],
  );

  if (!typeKey) {
    return null;
  }

  const renderLabel = (labelHtmlFor?: string) => (
    <DynamicQuestionLabel htmlFor={labelHtmlFor}>
      <div>
        {label}
        {!readOnly && isRequired && (
          <span className="required" aria-hidden>
            {' '}
            (Required)
          </span>
        )}
      </div>
      {isFlagged && (
        <StatusIndicator
          customColor={overdueMitigationColorResolver}
          label={'Flagged answer'}
          aria-label={'Flagged answer'}
        />
      )}
    </DynamicQuestionLabel>
  );

  const renderRequireError = () => (
    <HelpText
      state={FormState.ERROR}
      helpText="Answering this question is required"
      data-testid={`${baseTestId}-required-error`}
      textSize="medium"
      $margin="0"
    />
  );

  if (readOnly) {
    return (
      <ReadOnlyCard data-testid={baseTestId}>
        {renderLabel()}
        {description && (
          <DynamicQuestionDescription>{description}</DynamicQuestionDescription>
        )}
        <DynamicQuestionReadOnlyValue
          typeKey={typeKey}
          value={value}
          question={question}
          baseTestId={baseTestId}
          dropdownOptions={dropdownOptions}
          options={options}
          reportIdForFileDownload={reportIdForFileDownload}
          fileDownloadCaseManagerView={fileDownloadCaseManagerView}
        />
      </ReadOnlyCard>
    );
  }

  switch (typeKey) {
    case QuestionTypeTypeKey.SHORT_TEXT: {
      const showRequiredError =
        showValidation && isRequired && !value.text?.trim();
      return (
        <div data-testid={baseTestId}>
          {renderLabel()}
          {description && (
            <DynamicQuestionDescription>
              {description}
            </DynamicQuestionDescription>
          )}
          <TextInput
            state={showRequiredError ? FormState.ERROR : FormState.DEFAULT}
            value={value.text ?? ''}
            onChange={(e) =>
              onChange({ ...value, text: e.currentTarget.value })
            }
            data-testid={`${baseTestId}-input`}
            aria-required={isRequired}
          />
          {showRequiredError && renderRequireError()}
        </div>
      );
    }

    case QuestionTypeTypeKey.NUMBER: {
      const numericValue = Number(value.text ?? '');
      const outOfRangeError =
        value.text?.trim() !== '' &&
        !Number.isNaN(numericValue) &&
        (numericValue < 0 || numericValue > MAX_NUMBER);
      const showRequiredError =
        showValidation && isRequired && !value.text?.trim();
      return (
        <div data-testid={baseTestId}>
          {renderLabel()}
          {description && (
            <DynamicQuestionDescription>
              {description}
            </DynamicQuestionDescription>
          )}
          <TextInput
            type="number"
            state={
              showRequiredError || outOfRangeError
                ? FormState.ERROR
                : FormState.DEFAULT
            }
            value={value.text ?? ''}
            onChange={(e) =>
              onChange({ ...value, text: e.currentTarget.value })
            }
            data-testid={`${baseTestId}-input`}
            aria-required={isRequired}
          />
          {showRequiredError && renderRequireError()}
          {outOfRangeError && (
            <HelpText
              state={FormState.ERROR}
              helpText={`Value must be between 0 and ${MAX_NUMBER.toLocaleString()}`}
              data-testid={`${baseTestId}-range-error`}
              textSize="medium"
              $margin="0"
            />
          )}
        </div>
      );
    }

    case QuestionTypeTypeKey.DATE_TIME: {
      const showRequiredError =
        showValidation && isRequired && !value.text?.trim();
      return (
        <div data-testid={baseTestId}>
          {renderLabel()}
          {description && (
            <DynamicQuestionDescription>
              {description}
            </DynamicQuestionDescription>
          )}
          <DateTimeRow>
            <DatePicker
              value={value.text ?? ''}
              onChange={(e) =>
                onChange({
                  ...value,
                  text: e.target.value,
                })
              }
              error={showRequiredError}
              data-testid={`${baseTestId}-date-picker`}
              aria-required={isRequired}
            />
            {showRequiredError && renderRequireError()}
          </DateTimeRow>
        </div>
      );
    }

    case QuestionTypeTypeKey.TIME: {
      const inputId = `${baseTestId}-time-input`;
      const showRequiredError =
        showValidation && isRequired && !value.text?.trim();
      return (
        <div data-testid={baseTestId}>
          {renderLabel(inputId)}
          {description && (
            <DynamicQuestionDescription>
              {description}
            </DynamicQuestionDescription>
          )}
          <TimePicker
            id={inputId}
            value={value.text ?? ''}
            onChange={(e) =>
              onChange({
                ...value,
                text: e.target.value,
              })
            }
            data-testid={`${baseTestId}-time-picker`}
            aria-required={isRequired}
            error={showRequiredError}
          />
          {showRequiredError && renderRequireError()}
        </div>
      );
    }

    case QuestionTypeTypeKey.LONG_TEXT: {
      const showRequiredError =
        showValidation && isRequired && !value.text?.trim();
      return (
        <div data-testid={baseTestId}>
          {renderLabel()}
          {description && (
            <DynamicQuestionDescription>
              {description}
            </DynamicQuestionDescription>
          )}
          <TextArea
            state={showRequiredError ? FormState.ERROR : FormState.DEFAULT}
            value={value.text ?? ''}
            onChange={(e) =>
              onChange({ ...value, text: e.currentTarget.value })
            }
            data-testid={`${baseTestId}-textarea`}
            aria-required={isRequired}
          />
          {showRequiredError && renderRequireError()}
        </div>
      );
    }

    case QuestionTypeTypeKey.DROPDOWN: {
      const selectedOption =
        dropdownOptions.find((o) => o.value === value.selectedOptionId) ?? null;
      const showRequiredError = showValidation && isRequired && !selectedOption;
      return (
        <div data-testid={baseTestId}>
          {renderLabel()}
          {description && (
            <DynamicQuestionDescription>
              {description}
            </DynamicQuestionDescription>
          )}
          <DropdownSelect<{ value: string; label: string }>
            state={showRequiredError ? FormState.ERROR : FormState.DEFAULT}
            options={dropdownOptions}
            value={selectedOption}
            onChange={(
              opt: OnChangeValue<{ value: string; label: string }, boolean>,
            ) => {
              const single = Array.isArray(opt) ? opt[0] : opt;
              onChange({
                ...value,
                selectedOptionId: single?.value,
              });
            }}
            placeholder="Select an option"
            data-testid={`${baseTestId}-dropdown`}
          />
          {showRequiredError && renderRequireError()}
        </div>
      );
    }

    case QuestionTypeTypeKey.RADIO:
      return (
        <div data-testid={baseTestId}>
          {renderLabel()}
          {description && (
            <DynamicQuestionDescription>
              {description}
            </DynamicQuestionDescription>
          )}
          <OptionsList role="radiogroup" aria-required={isRequired}>
            {options.map((opt) => (
              <RadioButton
                key={`${baseTestId}-radio-${opt.id}-${
                  value.selectedOptionId ?? ''
                }`}
                id={`${baseTestId}-radio-${opt.id}`}
                label={opt.optionText ?? ''}
                labelPosition="right"
                defaultChecked={value.selectedOptionId === opt.id}
                onClick={() =>
                  onChange({
                    ...value,
                    selectedOptionId: opt.id ?? undefined,
                  })
                }
                data-testid={`${baseTestId}-radio-${opt.id}`}
              />
            ))}
          </OptionsList>
          {showValidation &&
            isRequired &&
            !value.selectedOptionId &&
            renderRequireError()}
        </div>
      );

    case QuestionTypeTypeKey.CHECKBOX:
    case QuestionTypeTypeKey.MULTIPLE_CHOICE: {
      const selectedIds = value.selectedOptionIds ?? [];
      return (
        <div data-testid={baseTestId}>
          {renderLabel()}
          {description && (
            <DynamicQuestionDescription>
              {description}
            </DynamicQuestionDescription>
          )}
          <OptionsList role="group">
            {options.map((opt) => {
              const optId = opt.id ?? '';
              const isChecked = selectedIds.includes(optId);
              return (
                <Checkbox
                  key={opt.id}
                  label={opt.optionText ?? ''}
                  labelPosition="right"
                  checked={isChecked}
                  $fullWidthLabel={true}
                  onChange={() => {
                    const next = isChecked
                      ? selectedIds.filter((id) => id !== optId)
                      : [...selectedIds, optId];
                    onChange({ ...value, selectedOptionIds: next });
                  }}
                  data-testid={`${baseTestId}-checkbox-${opt.id}`}
                />
              );
            })}
          </OptionsList>
          {showValidation &&
            isRequired &&
            selectedIds.length === 0 &&
            renderRequireError()}
        </div>
      );
    }

    case QuestionTypeTypeKey.DATA_GRID: {
      const dataGridConfig = question.dataGridConfig;
      if (!dataGridConfig?.columns?.length) {
        return null;
      }
      const dataGridRows = value.dataGridRows ?? [];
      return (
        <div data-testid={baseTestId}>
          {renderLabel()}
          {description && (
            <DynamicQuestionDescription>
              {description}
            </DynamicQuestionDescription>
          )}
          <DataGridInput
            config={dataGridConfig}
            rows={dataGridRows}
            onChange={(rows) => onChange({ ...value, dataGridRows: rows })}
            data-testid={`${baseTestId}-data-grid`}
          />
        </div>
      );
    }

    case QuestionTypeTypeKey.DATABASE_FIELD:
      return (
        <DynamicQuestionDatabaseField
          question={question}
          value={value}
          onChange={onChange}
          baseTestId={baseTestId}
          label={label}
          description={description}
          isRequired={isRequired}
        />
      );

    case QuestionTypeTypeKey.FILE_UPLOAD: {
      const evidenceFiles = value.evidenceUploadFiles ?? [];
      return (
        <DynamicQuestionFileUploadField
          baseTestId={baseTestId}
          renderLabel={renderLabel}
          description={description}
          reportIdForEvidenceUpload={reportIdForEvidenceUpload}
          fileUploadHelpArticleUrl={fileUploadHelpArticleUrl}
          evidenceFiles={evidenceFiles}
          handleSetPond={handleSetPond}
          syncEvidenceFilesFromPond={syncEvidenceFilesFromPond}
          handleRemoveFileFromPond={handleRemoveFileFromPond}
          skipEvidenceSyncAfterQaAttachmentRemove={Boolean(
            onDeleteQuestionnaireAttachment,
          )}
        />
      );
    }

    default:
      return null;
  }
};
